import React, { useState } from "react";
import { useIntl } from "react-intl";

export type MeERPScreenId =
  | "ER00" | "MD01" | "PR01" | "PR02" | "GR01" | "GR02"
  | "FN01" | "FN02" | "FN03" | "FN04" | "FN05" | "FN06" | "FN07" | "RP01";

interface ScreenVariant {
  label: string;
  columns: string[];
  details?: string[];
  actions?: string[];
  workflow?: string[];
}

interface ScreenTemplate {
  dependency: string;
  workflow: string[];
  filters: string[];
  details: string[];
  columns: string[];
  actions: string[];
  variants?: ScreenVariant[];
  next?: MeERPScreenId;
}

const SELECT_OPTIONS: Record<string, string[]> = {
  MEERP_VENDOR: [],
  MEERP_STORAGE: [],
  MEERP_REJECTED_STORAGE: [],
  MEERP_STATUS: [],
  MEERP_FIELD_DRUG_TYPE: [],
  MEERP_FIELD_DOCUMENT_TYPE: ["MEERP_OPTION_REQUEST_FORM", "MEERP_OPTION_PO", "MEERP_OPTION_RECEIPT", "MEERP_OPTION_VENDOR_SUMMARY", "MEERP_OPTION_PAYMENT_REPORT", "MEERP_OPTION_CHEQUE", "MEERP_OPTION_WITHHOLDING", "MEERP_OPTION_TAX_REPORT"],
  MEERP_FIELD_SELLER_TYPE: ["MEERP_OPTION_COMPANY", "MEERP_OPTION_EXEMPT_COMPANY", "MEERP_OPTION_GOVERNMENT"],
  MEERP_FIELD_TRANSACTION_TYPE: ["MEERP_OPTION_DRUG_DEPOSIT", "MEERP_OPTION_DENTAL_DEPOSIT", "MEERP_OPTION_TRANSFER"],
  MEERP_FIELD_EXEMPT: ["MEERP_OPTION_YES", "MEERP_OPTION_NO"],
};

const SCREEN_TEMPLATES: Partial<Record<MeERPScreenId, ScreenTemplate>> = {
  MD01: {
    dependency: "MEERP_DEP_PRODUCT_STOCK",
    workflow: ["MEERP_FLOW_SEARCH_MEDICINE", "MEERP_FLOW_CHECK_PRICE_STOCK", "MEERP_FLOW_OPEN_REQUEST"],
    filters: ["MEERP_FIELD_DRUG_CODE", "MEERP_FIELD_DRUG_NAME", "MEERP_FIELD_DRUG_TYPE", "MEERP_VENDOR", "MEERP_STORAGE"],
    details: ["MEERP_FIELD_DRUG_CODE", "MEERP_FIELD_DRUG_NAME", "MEERP_FIELD_SIZE", "MEERP_PACKAGE_UNIT", "MEERP_BASE_UNIT", "MEERP_CONVERSION_FACTOR", "MEERP_FIELD_VENDOR_CODE", "MEERP_FIELD_COST_PRICE", "MEERP_FIELD_SALE_PRICE", "MEERP_FIELD_REORDER_POINT", "MEERP_FIELD_STOCK_BALANCE", "MEERP_MANUFACTURED", "MEERP_EXPIRES"],
    columns: ["MEERP_FIELD_DRUG_CODE", "MEERP_FIELD_DRUG_NAME", "MEERP_FIELD_SIZE", "MEERP_VENDOR", "MEERP_FIELD_COST_PRICE", "MEERP_FIELD_SALE_PRICE", "MEERP_FIELD_REORDER_POINT", "MEERP_FIELD_STOCK_BALANCE"],
    actions: ["MEERP_ACTION_SEARCH", "MEERP_ACTION_OPEN_STOCK", "MEERP_ACTION_PRICE_HISTORY", "MEERP_ACTION_CALCULATE_SALE_PRICE", "MEERP_ACTION_CONFIRM_PRICE"], next: "PR02",
  },
  PR02: {
    dependency: "MEERP_DEP_PUR_WRITE_PRINT",
    workflow: ["MEERP_FLOW_REQUEST_DRAFT", "MEERP_FLOW_REQUEST_APPROVAL", "MEERP_FLOW_CREATE_PO", "MEERP_FLOW_PRINT_PO"],
    filters: [],
    details: ["MEERP_NUMBER", "MEERP_DATE", "MEERP_FIELD_REQUESTER", "MEERP_STORAGE", "MEERP_FIELD_REQUIRED_DATE", "MEERP_PURPOSE", "MEERP_VENDOR", "MEERP_FIELD_DELIVERY_DATE", "MEERP_FIELD_SUBTOTAL", "MEERP_FIELD_VAT", "MEERP_TOTAL"],
    columns: ["MEERP_FIELD_DRUG_CODE", "MEERP_PRODUCT", "MEERP_PACKAGE_UNIT", "MEERP_BASE_UNIT", "MEERP_CONVERSION_FACTOR", "MEERP_REQUESTED_QUANTITY", "MEERP_ORDERED_QUANTITY", "MEERP_PACKAGE_COST", "MEERP_BASE_UNIT_COST", "MEERP_FIELD_DISCOUNT", "MEERP_FIELD_VAT", "MEERP_TOTAL"],
    actions: ["MEERP_ACTION_ADD_MEDICINE", "MEERP_ACTION_SAVE_DRAFT", "MEERP_ACTION_EDIT", "MEERP_ACTION_PREVIEW_REQUEST", "MEERP_ACTION_SUBMIT_APPROVAL", "MEERP_ACTION_APPROVE", "MEERP_ACTION_CREATE_PO", "MEERP_ACTION_PREVIEW_PO", "MEERP_ACTION_PRINT", "MEERP_ACTION_REPRINT"], next: "GR01",
  },
  GR02: {
    dependency: "MEERP_DEP_RECEIVE_TRANSACTION",
    workflow: ["MEERP_FLOW_SELECT_PO", "MEERP_FLOW_ENTER_LOTS", "MEERP_FLOW_VERIFY_LINES", "MEERP_FLOW_SYNC_DESTINATIONS"],
    filters: [],
    details: ["MEERP_FIELD_PO_NUMBER", "MEERP_STORAGE", "MEERP_REJECTED_STORAGE", "MEERP_VENDOR", "MEERP_FIELD_RECEIPT_DATE", "MEERP_FIELD_DELIVERY_NOTE", "MEERP_FIELD_RECEIVED_BY", "MEERP_FIELD_STOCK_STATUS", "MEERP_FIELD_DISPENSE_STATUS", "MEERP_FIELD_ACCOUNTING_STATUS"],
    columns: ["MEERP_PRODUCT", "MEERP_FIELD_DRUG_TYPE", "MEERP_ORDERED_QUANTITY", "MEERP_ACCEPTED_TO_DATE", "MEERP_FIELD_OUTSTANDING", "MEERP_DELIVERED_QUANTITY", "MEERP_ACCEPTED_QUANTITY", "MEERP_REJECTED_QUANTITY", "MEERP_REJECTION_REASON", "MEERP_PACKAGE_UNIT", "MEERP_CONVERSION_FACTOR", "MEERP_STOCK_QUANTITY", "MEERP_REJECTED_STOCK_QUANTITY", "MEERP_BATCH", "MEERP_MANUFACTURED", "MEERP_EXPIRES", "MEERP_PACKAGE_COST", "MEERP_BASE_UNIT_COST", "MEERP_FIELD_VAT_TOTAL"],
    actions: ["MEERP_ACTION_ADD_LOT", "MEERP_ACTION_SAVE_DRAFT", "MEERP_ACTION_REVIEW", "MEERP_ACTION_CONFIRM_LINE", "MEERP_ACTION_PREVIEW_RECEIPT", "MEERP_ACTION_PRINT", "MEERP_ACTION_REPRINT"], next: "FN02",
  },
  FN01: {
    dependency: "MEERP_DEP_ACC_TAX_RULES",
    workflow: ["MEERP_FLOW_SET_SELLER_TYPE", "MEERP_FLOW_DEFINE_TAX_BASIS", "MEERP_FLOW_TEST_THRESHOLD", "MEERP_FLOW_ACTIVATE_RULE"],
    filters: ["MEERP_FIELD_SELLER_TYPE", "MEERP_FIELD_TAX_KIND", "MEERP_STATUS", "MEERP_FIELD_EFFECTIVE_DATE"],
    details: ["MEERP_FIELD_SELLER_TYPE", "MEERP_FIELD_TAX_BASIS", "MEERP_FIELD_THRESHOLD", "MEERP_FIELD_TAX_KIND", "MEERP_FIELD_TAX_RATE", "MEERP_FIELD_EXEMPT", "MEERP_FIELD_EFFECTIVE_DATE", "MEERP_FIELD_RULE_REFERENCE"],
    columns: ["MEERP_FIELD_SELLER_TYPE", "MEERP_FIELD_TAX_KIND", "MEERP_FIELD_TAX_RATE", "MEERP_FIELD_THRESHOLD", "MEERP_FIELD_EXEMPT", "MEERP_FIELD_EFFECTIVE_DATE", "MEERP_STATUS"],
    actions: ["MEERP_ACTION_ADD_RULE", "MEERP_ACTION_EDIT", "MEERP_ACTION_DELETE_DRAFT", "MEERP_ACTION_TEST_CALCULATION", "MEERP_ACTION_SAVE_DRAFT", "MEERP_ACTION_ACTIVATE", "MEERP_ACTION_DEACTIVATE"], next: "FN03",
  },
  FN02: {
    dependency: "MEERP_DEP_CONFIRMED_GR_SUMMARY",
    workflow: ["MEERP_FLOW_CHOOSE_PERIOD", "MEERP_FLOW_CHECK_CONFIRMED_GR", "MEERP_FLOW_SELECT_PAYABLE", "MEERP_FLOW_REVIEW_PAYMENT"],
    filters: ["MEERP_FIELD_PERIOD", "MEERP_STORAGE", "MEERP_VENDOR", "MEERP_FIELD_DRUG_TYPE"],
    details: ["MEERP_FIELD_CONFIRMATION_REF", "MEERP_FIELD_PO_NUMBER", "MEERP_FIELD_GR_NUMBER", "MEERP_FIELD_DELIVERY_NOTE", "MEERP_FIELD_CONFIRMED_QUANTITY", "MEERP_UNIT_COST"],
    columns: ["MEERP_VENDOR", "MEERP_STORAGE", "MEERP_FIELD_PO_COUNT", "MEERP_FIELD_GR_COUNT", "MEERP_FIELD_SUBTOTAL", "MEERP_FIELD_VAT", "MEERP_TOTAL", "MEERP_STATUS"],
    variants: [
      { label: "MEERP_VARIANT_DAILY", columns: ["MEERP_VENDOR", "MEERP_STORAGE", "MEERP_FIELD_PO_COUNT", "MEERP_FIELD_GR_COUNT", "MEERP_FIELD_SUBTOTAL", "MEERP_FIELD_VAT", "MEERP_TOTAL", "MEERP_STATUS"] },
      { label: "MEERP_VARIANT_MONTHLY", columns: ["MEERP_VENDOR", "MEERP_STORAGE", "MEERP_FIELD_PO_COUNT", "MEERP_FIELD_GR_COUNT", "MEERP_FIELD_SUBTOTAL", "MEERP_FIELD_VAT", "MEERP_TOTAL", "MEERP_STATUS"] },
      { label: "MEERP_VARIANT_YEARLY", columns: ["MEERP_VENDOR", "MEERP_STORAGE", "MEERP_FIELD_PO_COUNT", "MEERP_FIELD_GR_COUNT", "MEERP_FIELD_SUBTOTAL", "MEERP_FIELD_VAT", "MEERP_TOTAL", "MEERP_STATUS"] },
    ],
    actions: ["MEERP_ACTION_SEARCH", "MEERP_ACTION_OPEN_DETAIL", "MEERP_ACTION_REVIEW_PAYMENT"], next: "FN03",
  },
  FN03: {
    dependency: "MEERP_DEP_BANK_TAX_FORMULA",
    workflow: ["MEERP_FLOW_CHECK_CONFIRMED_GR", "MEERP_FLOW_APPLY_TAX_RULE", "MEERP_FLOW_REVIEW_NET", "MEERP_FLOW_CREATE_PAYMENT"],
    filters: [],
    details: ["MEERP_VENDOR", "MEERP_FIELD_SELLER_TYPE", "MEERP_FIELD_SUBTOTAL", "MEERP_FIELD_VAT", "MEERP_TOTAL", "MEERP_FIELD_THRESHOLD_BASIS", "MEERP_FIELD_TAX_BASIS", "MEERP_FIELD_WITHHOLDING", "MEERP_FIELD_ADJUSTMENT", "MEERP_FIELD_NET_PAYABLE"],
    columns: ["MEERP_FIELD_GR_NUMBER", "MEERP_PRODUCT", "MEERP_BATCH", "MEERP_FIELD_CONFIRMED_QUANTITY", "MEERP_UNIT_COST", "MEERP_TOTAL"],
    actions: ["MEERP_ACTION_SHOW_FORMULA", "MEERP_ACTION_RETURN_REVIEW", "MEERP_ACTION_CREATE_PAYMENT"], next: "FN04",
  },
  FN04: {
    dependency: "MEERP_DEP_ACC_PAYMENT_PRINT",
    workflow: ["MEERP_FLOW_REVIEW_PAYMENT", "MEERP_FLOW_SEND_APPROVAL", "MEERP_FLOW_APPROVE_PAYMENT", "MEERP_FLOW_PRINT_BANK_DOC"],
    filters: ["MEERP_FIELD_PERIOD", "MEERP_VENDOR", "MEERP_STATUS"],
    details: ["MEERP_FIELD_PAYMENT_NO", "MEERP_VENDOR", "MEERP_FIELD_BANK_ACCOUNT", "MEERP_FIELD_GR_NUMBER", "MEERP_FIELD_NET_PAYABLE", "MEERP_FIELD_APPROVER", "MEERP_FIELD_BANK_REF"],
    columns: ["MEERP_FIELD_PAYMENT_NO", "MEERP_VENDOR", "MEERP_DATE", "MEERP_FIELD_NET_PAYABLE", "MEERP_STATUS", "MEERP_FIELD_APPROVER"],
    variants: [
      { label: "MEERP_VARIANT_COMPANY_PAYMENT", columns: ["MEERP_FIELD_PAYMENT_NO", "MEERP_VENDOR", "MEERP_DATE", "MEERP_FIELD_NET_PAYABLE", "MEERP_STATUS", "MEERP_FIELD_APPROVER"] },
      {
        label: "MEERP_VARIANT_TAX_PAYMENT",
        columns: ["MEERP_FIELD_TAX_ENTRY", "MEERP_VENDOR", "MEERP_FIELD_DOCUMENT_REF", "MEERP_FIELD_PERIOD", "MEERP_FIELD_TAX_BASIS", "MEERP_FIELD_TAX_RATE", "MEERP_FIELD_TAX_AMOUNT", "MEERP_STATUS"],
        details: ["MEERP_FIELD_PERIOD", "MEERP_FIELD_TAX_KIND", "MEERP_FIELD_TAX_ENTRY", "MEERP_FIELD_DOCUMENT_REF", "MEERP_FIELD_TAX_BASIS", "MEERP_FIELD_TAX_AMOUNT", "MEERP_FIELD_APPROVER", "MEERP_FIELD_TEMPLATE_VERSION"],
        workflow: ["MEERP_FLOW_CHOOSE_TAX_PERIOD", "MEERP_FLOW_REVIEW_TAX_SOURCE", "MEERP_FLOW_REVIEW_TAX_TOTAL", "MEERP_FLOW_CREATE_TAX_REPORT", "MEERP_FLOW_PRINT_TAX_REPORT"],
        actions: ["MEERP_ACTION_REVIEW_TOTAL", "MEERP_ACTION_CREATE_TAX_REPORT", "MEERP_ACTION_PREVIEW_TAX_REPORT", "MEERP_ACTION_PRINT_FOR_APPROVAL", "MEERP_ACTION_SEND_APPROVAL", "MEERP_ACTION_PRINT_APPROVED"],
      },
    ],
    actions: ["MEERP_ACTION_REVIEW_TOTAL", "MEERP_ACTION_SEND_APPROVAL", "MEERP_ACTION_APPROVE_PAYMENT", "MEERP_ACTION_PRINT_APPROVAL", "MEERP_ACTION_PRINT_CHEQUE", "MEERP_ACTION_PRINT_WITHHOLDING"], next: "RP01",
  },
  FN05: {
    dependency: "MEERP_DEP_ACC_JOURNAL_LEDGER",
    workflow: ["MEERP_FLOW_CHOOSE_TRANSACTION", "MEERP_FLOW_ENTER_DEBIT_CREDIT", "MEERP_FLOW_CHECK_BALANCE", "MEERP_FLOW_POST_LEDGER"],
    filters: ["MEERP_FIELD_PERIOD", "MEERP_FIELD_DOCUMENT_REF", "MEERP_FIELD_TRANSACTION_TYPE", "MEERP_STATUS"],
    details: ["MEERP_FIELD_ACCOUNTING_DATE", "MEERP_FIELD_DOCUMENT_DATE", "MEERP_FIELD_TRANSACTION_TYPE", "MEERP_FIELD_DOCUMENT_REF", "MEERP_FIELD_ACCOUNT_CODE", "MEERP_FIELD_DESCRIPTION", "MEERP_FIELD_DEBIT", "MEERP_FIELD_CREDIT"],
    columns: ["MEERP_FIELD_DOCUMENT_REF", "MEERP_FIELD_ACCOUNTING_DATE", "MEERP_FIELD_TRANSACTION_TYPE", "MEERP_FIELD_DEBIT", "MEERP_FIELD_CREDIT", "MEERP_STATUS"],
    variants: [
      { label: "MEERP_VARIANT_JOURNAL", columns: ["MEERP_FIELD_DOCUMENT_REF", "MEERP_FIELD_ACCOUNTING_DATE", "MEERP_FIELD_TRANSACTION_TYPE", "MEERP_FIELD_DEBIT", "MEERP_FIELD_CREDIT", "MEERP_STATUS"] },
      {
        label: "MEERP_VARIANT_LEDGER",
        columns: ["MEERP_FIELD_ACCOUNT_CODE", "MEERP_FIELD_ACCOUNT_NAME", "MEERP_FIELD_OPENING_BALANCE", "MEERP_FIELD_DEBIT", "MEERP_FIELD_CREDIT", "MEERP_FIELD_CLOSING_BALANCE"],
        details: ["MEERP_FIELD_ACCOUNT_CODE", "MEERP_FIELD_ACCOUNT_NAME", "MEERP_FIELD_PERIOD", "MEERP_FIELD_OPENING_BALANCE", "MEERP_FIELD_DEBIT", "MEERP_FIELD_CREDIT", "MEERP_FIELD_CLOSING_BALANCE"],
        workflow: ["MEERP_FLOW_CHOOSE_ACCOUNT", "MEERP_FLOW_REVIEW_LEDGER", "MEERP_FLOW_PRINT_DOCUMENT"],
        actions: ["MEERP_ACTION_SEARCH", "MEERP_ACTION_PRINT"],
      },
    ],
    actions: ["MEERP_ACTION_ADD_JOURNAL", "MEERP_ACTION_CHECK_BALANCE", "MEERP_ACTION_POST", "MEERP_ACTION_PRINT"], next: "FN06",
  },
  FN06: {
    dependency: "MEERP_DEP_ACC_COA_MAPPING",
    workflow: ["MEERP_FLOW_CREATE_ACCOUNT", "MEERP_FLOW_MAP_SOURCE", "MEERP_FLOW_VALIDATE_MAPPING"],
    filters: ["MEERP_FIELD_ACCOUNT_CODE", "MEERP_FIELD_ACCOUNT_NAME", "MEERP_FIELD_ACCOUNT_CATEGORY", "MEERP_STATUS"],
    details: ["MEERP_FIELD_ACCOUNT_CODE", "MEERP_FIELD_ACCOUNT_NAME", "MEERP_FIELD_ACCOUNT_CATEGORY", "MEERP_FIELD_PARENT_ACCOUNT", "MEERP_FIELD_EFFECTIVE_DATE", "MEERP_FIELD_SOURCE_TYPE", "MEERP_FIELD_COST_CENTER"],
    columns: ["MEERP_FIELD_ACCOUNT_CODE", "MEERP_FIELD_ACCOUNT_NAME", "MEERP_FIELD_ACCOUNT_CATEGORY", "MEERP_FIELD_PARENT_ACCOUNT", "MEERP_FIELD_SOURCE_TYPE", "MEERP_STATUS"],
    actions: ["MEERP_ACTION_ADD_ACCOUNT", "MEERP_ACTION_EDIT", "MEERP_ACTION_DEACTIVATE", "MEERP_ACTION_VALIDATE_MAPPING"], next: "FN07",
  },
  FN07: {
    dependency: "MEERP_DEP_TREATMENT_FEED",
    workflow: ["MEERP_FLOW_RECEIVE_TREATMENT", "MEERP_FLOW_MAP_SOURCE", "MEERP_FLOW_SEND_ACCOUNTING", "MEERP_FLOW_CHECK_ACK"],
    filters: ["MEERP_FIELD_TREATMENT_DATE", "MEERP_FIELD_ACCOUNTING_DATE", "MEERP_STORAGE", "MEERP_STATUS"],
    details: ["MEERP_FIELD_BATCH_NO", "MEERP_FIELD_SOURCE_COUNT", "MEERP_FIELD_UNMAPPED_COUNT", "MEERP_FIELD_DEBIT", "MEERP_FIELD_CREDIT", "MEERP_FIELD_SENT_AT", "MEERP_FIELD_ACK_STATUS", "MEERP_FIELD_ERROR_REASON"],
    columns: ["MEERP_FIELD_BATCH_NO", "MEERP_FIELD_TREATMENT_DATE", "MEERP_FIELD_SOURCE_COUNT", "MEERP_FIELD_UNMAPPED_COUNT", "MEERP_FIELD_SENT_AT", "MEERP_FIELD_ACK_STATUS"],
    actions: ["MEERP_ACTION_CHECK_DATA", "MEERP_ACTION_OPEN_ERRORS", "MEERP_ACTION_RECHECK", "MEERP_ACTION_RETRY_FAILED"],
  },
  RP01: {
    dependency: "MEERP_DEP_BANK_TEMPLATES",
    workflow: ["MEERP_FLOW_FIND_DOCUMENT", "MEERP_FLOW_CHECK_VERSION", "MEERP_FLOW_PREVIEW_DOCUMENT", "MEERP_FLOW_PRINT_DOCUMENT"],
    filters: ["MEERP_FIELD_DOCUMENT_TYPE", "MEERP_FIELD_PERIOD", "MEERP_STORAGE", "MEERP_VENDOR", "MEERP_FIELD_DOCUMENT_REF", "MEERP_STATUS"],
    details: ["MEERP_FIELD_DOCUMENT_TYPE", "MEERP_FIELD_DOCUMENT_REF", "MEERP_FIELD_VERSION", "MEERP_FIELD_CREATED_BY", "MEERP_FIELD_PRINT_COUNT", "MEERP_FIELD_TEMPLATE_VERSION"],
    columns: ["MEERP_FIELD_DOCUMENT_REF", "MEERP_FIELD_DOCUMENT_TYPE", "MEERP_DATE", "MEERP_FIELD_VERSION", "MEERP_FIELD_CREATED_BY", "MEERP_FIELD_PRINT_COUNT", "MEERP_STATUS"],
    variants: [
      { label: "MEERP_VARIANT_PUR_DOCUMENTS", columns: ["MEERP_FIELD_DOCUMENT_REF", "MEERP_FIELD_DOCUMENT_TYPE", "MEERP_DATE", "MEERP_FIELD_VERSION", "MEERP_FIELD_CREATED_BY", "MEERP_FIELD_PRINT_COUNT", "MEERP_STATUS"] },
      { label: "MEERP_VARIANT_ACC_DOCUMENTS", columns: ["MEERP_FIELD_DOCUMENT_REF", "MEERP_FIELD_DOCUMENT_TYPE", "MEERP_DATE", "MEERP_FIELD_VERSION", "MEERP_FIELD_CREATED_BY", "MEERP_FIELD_PRINT_COUNT", "MEERP_STATUS"] },
    ],
    actions: ["MEERP_ACTION_SEARCH", "MEERP_ACTION_PREVIEW", "MEERP_ACTION_PRINT", "MEERP_ACTION_REPRINT"],
  },
};

interface MeERPScreenScaffoldProps {
  screen: MeERPScreenId;
  onNavigate: (screen: MeERPScreenId) => void;
}

const MeERPScreenScaffold: React.FC<MeERPScreenScaffoldProps> = ({ screen, onNavigate }) => {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const template = SCREEN_TEMPLATES[screen];
  const [variantIndex, setVariantIndex] = useState(0);
  if (!template) return null;
  const variant = template.variants?.[variantIndex];
  const columns = variant?.columns || template.columns;
  const details = variant?.details || template.details;
  const workflow = variant?.workflow || template.workflow;
  const actions = variant?.actions || template.actions;
  const renderField = (field: string) => <label key={field}>{t(field)}
    {SELECT_OPTIONS[field] ? <select disabled defaultValue="">
      <option value="">{t("MEERP_AWAITING_DATA")}</option>
      {SELECT_OPTIONS[field].map((option) => <option key={option} value={option}>{t(option)}</option>)}
    </select> : <input type="text" disabled placeholder={t("MEERP_AWAITING_DATA")} />}
  </label>;

  return <div className="card-meerp__scaffold">
    <div className="card-meerp__page-heading">
      <div><h2>{t(`MEERP_SCREEN_${screen}`)}</h2></div>
      <span className="card-meerp__coming-soon">{t("MEERP_SCAFFOLD_BADGE")}</span>
    </div>
    <p className="card-meerp__muted">{t("MEERP_SCAFFOLD_NOTICE")}</p>
    {screen === "GR02" && <p className="card-meerp__muted">{t("MEERP_RECEIPT_QUANTITY_NOTE")}</p>}
    {(screen === "FN02" || screen === "FN03" || screen === "FN04") && <p className="card-meerp__muted">{t("MEERP_BILLING_NOT_PAYMENT")}</p>}
    <p className="card-meerp__dependency"><strong>{t("MEERP_DEPENDENCY_LABEL")}</strong> {t(template.dependency)}</p>

    <section className="card-meerp__scaffold-section" aria-label={t("MEERP_WORKFLOW_LABEL")}>
      <h3>{t("MEERP_WORKFLOW_LABEL")}</h3>
      <ol className="card-meerp__workflow">{workflow.map((step) => <li key={step}>{t(step)}</li>)}</ol>
    </section>

    {template.variants && <div className="card-meerp__tabs" role="group" aria-label={t("MEERP_VIEW_LABEL")}>
      {template.variants.map((variant, index) => <button className="card-meerp__tab" type="button" key={variant.label} aria-pressed={variantIndex === index} onClick={() => setVariantIndex(index)}>{t(variant.label)}</button>)}
    </div>}

    {template.filters.length > 0 && <section className="card-meerp__scaffold-section" aria-label={t("MEERP_FILTERS_LABEL")}>
      <h3>{t("MEERP_FILTERS_LABEL")}</h3>
      <div className="card-meerp__field-grid">{template.filters.map(renderField)}</div>
    </section>}

    <section className="card-meerp__scaffold-section" aria-label={t("MEERP_DETAILS_LABEL")}>
      <h3>{t("MEERP_DETAILS_LABEL")}</h3>
      <div className="card-meerp__field-grid">{details.map(renderField)}</div>
    </section>

    <section className="card-meerp__scaffold-section" aria-label={t("MEERP_ITEMS")}>
      <h3>{t("MEERP_LIST_PREVIEW_LABEL")}</h3>
      <div className="card-meerp__table-wrap"><table className="card-meerp__table"><thead><tr>{columns.map((column, index) => <th scope="col" key={`${column}-${index}`}>{t(column)}</th>)}</tr></thead>
        <tbody><tr><td colSpan={columns.length} className="card-meerp__awaiting-cell">{t("MEERP_AWAITING_DATA")}</td></tr></tbody>
      </table></div>
    </section>

    <div className="card-meerp__action-bar" aria-label={t("MEERP_ACTIONS_LABEL")}>{actions.map((action) => <button className="card-meerp__button" type="button" key={action} disabled title={t(template.dependency)}>{t(action)}</button>)}</div>
    {template.next && <button type="button" className="card-meerp__prototype-link" onClick={() => onNavigate(template.next as MeERPScreenId)}>{t("MEERP_VIEW_NEXT_SCAFFOLD")} → {t(`MEERP_SCREEN_${template.next}`)}</button>}
  </div>;
};

export default MeERPScreenScaffold;
